import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, ShieldCheck, Trash, PencilSimple, Lock, X, Check } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_LABELS = {
  listings: 'Listings',
  users: 'Users',
  reviews: 'Reviews',
  admin: 'Admin Panel',
  communication: 'Notifications & Email',
  config: 'Configuration',
  system: 'System',
};

// Group permissions by their `group` field for the matrix UI.
const groupPermissions = (permissions) => {
  const groups = {};
  permissions.forEach((p) => {
    if (!groups[p.group]) groups[p.group] = [];
    groups[p.group].push(p);
  });
  return groups;
};

// ─── Role Editor Modal ────────────────────────────────────────────
const RoleEditorModal = ({ open, onOpenChange, role, permissions, onSaved }) => {
  const isEdit = Boolean(role);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (role) {
      setName(role.name || '');
      setDescription(role.description || '');
      setEnabled(new Set(role.permissions || []));
    } else {
      setName('');
      setDescription('');
      setEnabled(new Set());
    }
  }, [role, open]);

  const togglePerm = (key) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const grouped = useMemo(() => groupPermissions(permissions), [permissions]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Role name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        permissions: Array.from(enabled),
      };
      if (isEdit) {
        // Default roles: only permissions can change.
        const body = role.is_default
          ? { permissions: payload.permissions }
          : payload;
        await axios.put(`${API}/admin/roles/${role.role_id}`, body, { withCredentials: true });
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/admin/roles`, payload, { withCredentials: true });
        toast.success('Role created');
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const isLocked = role?.is_default;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="role-editor-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <PencilSimple size={20} /> : <Plus size={20} />}
            {isEdit ? `Edit role: ${role.name}` : 'Create new role'}
            {isLocked && (
              <Badge className="bg-surface_muted text-text_main border border-border text-[10px]">
                <Lock size={10} className="mr-1" /> Default
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="role-name" className="text-xs font-medium">Role name</Label>
            <Input
              id="role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLocked}
              placeholder="e.g. Support Agent"
              className="border border-border mt-1"
              data-testid="role-name-input"
            />
            {isLocked && (
              <p className="text-[11px] text-text_muted mt-1">
                Default roles cannot be renamed. You can still modify permissions.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="role-desc" className="text-xs font-medium">Description (optional)</Label>
            <Input
              id="role-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLocked}
              placeholder="What can this role do?"
              className="border border-border mt-1"
              data-testid="role-description-input"
            />
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="text-sm font-semibold mb-3">Permissions</h3>
            <div className="space-y-5">
              {Object.entries(grouped).map(([groupKey, groupPerms]) => (
                <div key={groupKey} data-testid={`perm-group-${groupKey}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-xs font-semibold text-text_main uppercase tracking-wide">
                      {GROUP_LABELS[groupKey] || groupKey}
                    </h4>
                    <span className="text-[11px] text-text_muted">
                      ({groupPerms.filter((p) => enabled.has(p.key)).length}/{groupPerms.length})
                    </span>
                  </div>
                  <div className="space-y-2 pl-2">
                    {groupPerms.map((p) => (
                      <label
                        key={p.key}
                        className="flex items-start gap-3 cursor-pointer select-none"
                        data-testid={`perm-row-${p.key}`}
                      >
                        <Switch
                          checked={enabled.has(p.key)}
                          onCheckedChange={() => togglePerm(p.key)}
                          data-testid={`perm-toggle-${p.key}`}
                        />
                        <div className="flex-1 -mt-0.5">
                          <p className="text-sm font-medium">{p.description}</p>
                          <p className="text-[11px] text-text_muted font-mono">{p.key}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="role-editor-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary-hover text-white"
            data-testid="role-editor-save"
          >
            {saving ? 'Saving…' : (
              <><Check size={16} weight="bold" className="mr-1.5" />{isEdit ? 'Save changes' : 'Create role'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Delete Modal (with reassignment) ─────────────────────────────
const DeleteRoleDialog = ({ open, onOpenChange, role, allRoles, onDeleted }) => {
  const [reassignTo, setReassignTo] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setReassignTo('');
  }, [role, open]);

  if (!role) return null;
  const hasMembers = (role.user_count || 0) > 0;
  const reassignTargets = allRoles.filter((r) => r.role_key !== role.role_key);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const url = `${API}/admin/roles/${role.role_id}` +
        (hasMembers && reassignTo ? `?reassign_to=${encodeURIComponent(reassignTo)}` : '');
      await axios.delete(url, { withCredentials: true });
      toast.success(`Role "${role.name}" deleted`);
      onDeleted();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-role-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete role "{role.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            {hasMembers
              ? `${role.user_count} user(s) currently use this role. Pick a role to migrate them to.`
              : 'No users are assigned to this role. This action cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {hasMembers && (
          <div className="py-2">
            <Label className="text-xs font-medium">Migrate users to:</Label>
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger className="border border-border mt-1" data-testid="reassign-select">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {reassignTargets.map((r) => (
                  <SelectItem key={r.role_key} value={r.role_key}>
                    {r.name}{r.is_default ? ' (default)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-role-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={busy || (hasMembers && !reassignTo)}
            className="bg-danger hover:bg-danger text-white"
            data-testid="delete-role-confirm"
          >
            {busy ? 'Deleting…' : 'Delete role'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// ─── Main Section ─────────────────────────────────────────────────
export const RolesManagement = () => {
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [permsRes, rolesRes] = await Promise.all([
        axios.get(`${API}/admin/permissions`, { withCredentials: true }),
        axios.get(`${API}/admin/roles`, { withCredentials: true }),
      ]);
      setPermissions(permsRes.data.permissions || []);
      setRoles(rolesRes.data.roles || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };
  const openEdit = (role) => {
    setEditing(role);
    setEditorOpen(true);
  };
  const openDelete = (role) => {
    setDeleting(role);
    setDeleteOpen(true);
  };

  if (loading) return <div className="text-text_muted text-sm">Loading roles…</div>;

  return (
    <div data-testid="roles-management">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-text_main mb-1 flex items-center gap-2">
            <ShieldCheck size={22} weight="bold" /> Roles & Permissions
          </h2>
          <p className="text-sm text-text_muted">
            Define who can do what across the platform. Toggle permissions per role; changes apply instantly.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-primary hover:bg-primary-hover text-white"
          data-testid="create-role-btn"
        >
          <Plus size={16} weight="bold" className="mr-1.5" />
          Create role
        </Button>
      </div>

      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface text-text_muted text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-4">Role</th>
                <th className="text-left p-4">Permissions</th>
                <th className="text-left p-4">Users</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {roles.map((role) => (
                <tr key={role.role_id} data-testid={`role-row-${role.role_key}`}>
                  <td className="p-4 align-top">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-text_main">{role.name}</span>
                      {role.is_default && (
                        <Badge className="bg-info-soft text-info border border-info text-[10px]">
                          <Lock size={10} className="mr-1" /> Default
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-text_muted">{role.description || '—'}</p>
                    <p className="text-[11px] text-text_muted font-mono mt-0.5">{role.role_key}</p>
                  </td>
                  <td className="p-4 align-top">
                    <span className="font-medium" data-testid={`role-perm-count-${role.role_key}`}>
                      {role.permissions.length}
                    </span>
                    <span className="text-text_muted"> / {permissions.length}</span>
                  </td>
                  <td className="p-4 align-top">
                    <span className="font-medium">{role.user_count}</span>
                  </td>
                  <td className="p-4 align-top text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border border-border"
                        onClick={() => openEdit(role)}
                        data-testid={`role-edit-${role.role_key}`}
                      >
                        <PencilSimple size={14} className="mr-1" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border border-border text-danger hover:bg-danger-soft hover:text-danger disabled:opacity-40 disabled:hover:bg-transparent"
                        disabled={role.is_default}
                        title={role.is_default ? 'Default roles cannot be deleted' : 'Delete role'}
                        onClick={() => openDelete(role)}
                        data-testid={`role-delete-${role.role_key}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <RoleEditorModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        role={editing}
        permissions={permissions}
        onSaved={fetchAll}
      />
      <DeleteRoleDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        role={deleting}
        allRoles={roles}
        onDeleted={fetchAll}
      />
    </div>
  );
};
